import React from "react";
import "./Tile.scss";
import AppLink from "../AppLink/AppLink";
import { Button } from "@brightcove/studio-components";
import { SVGImage } from "../../assets/images";
import classnames from "classnames";
import { isEmpty } from "lodash";
import {
  formatDate,
  getPerformanceYearRange,
  getCastByType,
  isShortFormContent,
} from "../../utils";
import { tileBadge } from "./mediaTypeIcon";

class Tile extends React.Component<any, any> {
  ctaWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();

  componentDidMount() {
    this.patchInnerButton();
  }

  componentDidUpdate() {
    this.patchInnerButton();
  }

  patchInnerButton() {
    // MET2-2888 (revised): make the inner Watch Now / Listen Now button the
    // keyboard Tab stop with a descriptive aria-label, so VoiceOver
    // announces e.g. "Tristan und Isolde, Watch Now" — not just "Watch Now"
    // (the original bug) and not the outer link (the prior fix, which made
    // Kevin's focus land on the whole tile instead of the visible CTA).
    //
    // The outer AppLink stays in the DOM for mouse click-anywhere behavior
    // (see render: tabIndex=-1 keeps it out of the Tab order, so we only
    // get one Tab stop per tile).
    //
    // @brightcove/studio-components Button drops props it doesn't know
    // about (tabIndex, aria-label) — set them imperatively after render.
    const wrap = this.ctaWrapperRef.current;
    if (!wrap) return;
    const btn = wrap.querySelector("button");
    if (!btn) return;
    btn.setAttribute("aria-label", this.computeCtaAriaLabel());
    btn.removeAttribute("inert");
    btn.removeAttribute("aria-hidden");
    btn.setAttribute("tabindex", "0");
  }

  computeCtaAriaLabel() {
    const { ctaLabel, mediaType, contentType, header } = this.props;
    const cta =
      ctaLabel ||
      (mediaType === "audio" ||
      mediaType === "audio_stream" ||
      mediaType === "short_form_audio"
        ? "Listen Now"
        : contentType === "screen"
        ? "Explore"
        : "Watch Now");
    const headerText = (header || "").replace(/<[^>]*>/g, "").trim();
    return headerText ? `${headerText}, ${cta}` : cta;
  }

  getCastAndCrew = (cast) => {
    const composers = cast
      .filter((cast) => {
        return (
          cast.role !== "COMPOSER" ||
          cast.role !== "LIBRETTIST" ||
          cast.role !== "CONDUCTOR" ||
          cast.role !== "GROUP"
        );
      })
      .map((cast) => cast.person.name);
    return composers.length ? composers.join(", ") : "";
  };

  render() {
    const {
      contentType,
      mediaType,
      header,
      runTime,
      resolutionType,
      previewableChapters,
      hasAudioDescription,
      hasCaptions,
      imageurl,
      badge,
      performanceDateISO,
      cast,
      url,
      position,
      ctaLabel,
      tier,
      shortCast,
      subHeader,
      description,
    } = this.props;
    const performanceYearRange = getPerformanceYearRange(performanceDateISO);
    const composer = (cast && getCastByType(cast, "composer")) || "";
    const corner = tileBadge({
      mediaType,
      contentType,
      tier,
      previewableChapters,
      isShortForm: isShortFormContent(mediaType),
    });
    const isDetailPage =
      window.location.pathname.indexOf("/performance/detail") > -1;

    // MET2-2888 (revised): the inner button is the Tab stop now (see
    // patchInnerButton). The outer link stays for mouse "click anywhere
    // on the tile" behavior but is removed from the Tab order via
    // tabIndex=-1, so we have exactly one Tab stop per tile.
    const tileCtaLabel =
      ctaLabel ||
      (mediaType === "audio" ||
      mediaType === "audio_stream" ||
      mediaType === "short_form_audio"
        ? "Listen Now"
        : contentType === "screen"
        ? "Explore"
        : "Watch Now");

    return (
      <AppLink to={url} tabIndex={-1}>
        <div
          className={classnames(
            "Tile",
            { "short-form": isShortFormContent(mediaType) },
            contentType
          )}
        >
          <div
            className={classnames(
              "Tile-image-container",
              mediaType === "audio"
                ? `audio-${performanceYearRange}s`
                : ""
            )}
          >
            {mediaType !== "audio" &&
              imageurl && <img src={imageurl} alt="" />}
            {corner.icon && (
                <span className="Tile-item-mediatype-icon">
                  {corner.icon === "audio" ? (
                    <img src={SVGImage.Audio} alt="Audio Icon" />
                  ) : (
                    <img src={SVGImage.PlayCircle} alt="Play Icon" />
                  )}
                </span>
              )}
            {corner.freePreview && (
                <span className="Tile-item-free-preview">
                  <img src={SVGImage.FreePreview} alt="Free Preview" />
                </span>
              )}
            {(mediaType === "audio") && (
              <div className="Tile-image-container-overlay">
                <p>Live Audio Recording</p>
                <p>{performanceYearRange}s</p>
              </div>
            )}
            {resolutionType && resolutionType === "HD" && (
              <p className="Tile-marker left hd">
                <span>{resolutionType}</span>
              </p>
            )}
            {(hasCaptions || hasAudioDescription) && (
              <p className="Tile-marker right">
                {hasCaptions && <span className="Tile-marker-cc">CC</span>}
                {hasAudioDescription && (
                  <span className="Tile-marker-ad">AD</span>
                )}
              </p>
            )}
            {position && runTime && (
              <div className="Tile-progress">
                <div
                  className="Tile-progress-bar"
                  style={{ width: `${(position / runTime) * 100}%` }}
                />
              </div>
            )}
          </div>
          <div className="Tile-body">
            {!isShortFormContent(mediaType) && (
              <div
                className={classnames(
                  "Tile-body-metadata",
                  (subHeader || composer) && performanceDateISO ? "has-medatadata" : ""
                )}
              >
                {((!isEmpty(subHeader) || !isEmpty(composer)) || performanceDateISO) && (
                  // MET2-2876 / MET2-2629: wrap sub-header + date in one text
                  // block so a long sub-header wraps and stays fully visible
                  // (no ellipsis), while the badge stays pinned top-right.
                  <span className="Tile-body-metadata-text">
                    {(!isEmpty(subHeader) || !isEmpty(composer)) && (
                      <span className="Tile-body-pre-title">{!isEmpty(subHeader) ? subHeader : composer}</span>
                    )}
                    {performanceDateISO && (
                      <span className="Tile-body-performance-date">
                        {formatDate(performanceDateISO)}
                      </span>
                    )}
                  </span>
                )}
                {badge && <span className="Tile-body-badge">{badge}!</span>}
              </div>
            )}
            <h2
              className="Tile-body-header"
              dangerouslySetInnerHTML={{ __html: header }}
            />
            {shortCast && !isShortFormContent(mediaType) ? (
              <div className="Tile-body-cast">
                <div dangerouslySetInnerHTML={{ __html: shortCast }}></div>
              </div>
            ) : (

              <div className="Tile-body-cast">
                <div dangerouslySetInnerHTML={{ __html: description }}></div>
              </div>
            )}
          </div>
          <div className="Tile-cta" ref={this.ctaWrapperRef}>
            {/* MET2-2888: the inner Button IS the keyboard Tab stop and
                carries the descriptive aria-label ("[Title], Watch Now").
                tabIndex / aria-label are set imperatively in
                patchInnerButton() because studio-components Button drops
                those props at render time. The button's onClick returns
                false and the click bubbles up to the parent AppLink,
                which performs the actual client-side navigation. */}
            <Button
              theme="classic"
              text={tileCtaLabel}
              className={classnames(
                "Tile-cta btn",
                isDetailPage
                  ? "btn-transparent white"
                  : mediaType === "audio"
                  ? "btn-transparent black"
                  : "btn-pink"
              )}
              onClick={() => {
                return false;
              }}
            />
          </div>
        </div>
      </AppLink>
    );
  }
}

export default Tile;
